import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import {
  productCreationRequestService,
  productService,
  productGroupService,
  brandService,
  unitService
} from '../../../../services/erpApi';
import { useToast } from '../../../../hooks/useToast';
import { usePermissions } from '../../../../hooks/usePermissions';
import { MODULES, ACTIONS } from '../../../../utils/permissions';
import { Toast } from '../../../../components';
import FormField from '../../../../components/ERP/Form/FormField';
import './ProductCreationRequestFormPage.css';

interface FormData {
  sku: string;
  barcode: string;
  name: string;
  description: string;
  costPrice: string;
  price: string;
  groupId: string;
  brandId: string;
  unitId: string;
  minStock: string;
  maxStock: string;
  active: boolean;
}

const emptyForm: FormData = {
  sku: '',
  barcode: '',
  name: '',
  description: '',
  costPrice: '',
  price: '',
  groupId: '',
  brandId: '',
  unitId: '',
  minStock: '',
  maxStock: '',
  active: true
};

const parsePayload = (payload: any): Record<string, any> => {
  if (!payload) return {};
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {
      return {};
    }
  }
  return payload;
};

const ProductCreationRequestFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCT_REQUEST_CREATE);
  const canApprove = hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCT_REQUEST_APPROVE);
  const canEditRequest = canCreate || canApprove;
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingSku, setLoadingSku] = useState(false);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [grupos, setGrupos] = useState<any[]>([]);
  const [marcas, setMarcas] = useState<any[]>([]);
  const [unidades, setUnidades] = useState<any[]>([]);

  const loadOptions = useCallback(async () => {
    try {
      const [gruposRes, marcasRes, unidadesRes] = await Promise.all([
        productGroupService.getAll(),
        brandService.getAll(),
        unitService.getAll()
      ]);
      setGrupos(gruposRes.success ? (gruposRes.data || []) : (Array.isArray(gruposRes) ? gruposRes : []));
      setMarcas(marcasRes.success ? (marcasRes.data || []) : (Array.isArray(marcasRes) ? marcasRes : []));
      setUnidades(unidadesRes.success ? (unidadesRes.data || []) : (Array.isArray(unidadesRes) ? unidadesRes : []));
    } catch (error) {
      console.error('Erro ao carregar opções:', error);
    }
  }, []);

  const loadNextSku = useCallback(async () => {
    setLoadingSku(true);
    try {
      const response = await productService.getNextSku();
      if (response.success && response.data?.sku) {
        setFormData(prev => ({ ...prev, sku: response.data.sku }));
      }
    } catch (error) {
      console.error('Erro ao obter próximo SKU:', error);
    } finally {
      setLoadingSku(false);
    }
  }, []);

  const loadRequest = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await productCreationRequestService.getById(id);
      if (response.success && response.data) {
        const request = response.data;

        if (request.status !== 'pending') {
          showToast('Apenas solicitações pendentes podem ser editadas', 'warning');
          navigate(`/erp/produtos/solicitacoes/${id}`);
          return;
        }

        if (!canEditRequest) {
          showToast('Você não tem permissão para editar esta solicitação', 'error');
          navigate(`/erp/produtos/solicitacoes/${id}`);
          return;
        }

        const payload = parsePayload(request.payload);
        setFormData({
          sku: payload.sku || '',
          barcode: payload.barcode || '',
          name: payload.name || '',
          description: payload.description || '',
          costPrice: payload.costPrice != null ? String(payload.costPrice) : '',
          price: payload.price != null ? String(payload.price) : '',
          groupId: payload.groupId != null ? String(payload.groupId) : '',
          brandId: payload.brandId != null ? String(payload.brandId) : '',
          unitId: payload.unitId != null ? String(payload.unitId) : '',
          minStock: payload.minStock != null ? String(payload.minStock) : '',
          maxStock: payload.maxStock != null ? String(payload.maxStock) : '',
          active: payload.active !== undefined ? Boolean(payload.active) : true
        });
      } else {
        showToast('Solicitação não encontrada', 'error');
        navigate('/erp/produtos/solicitacoes');
      }
    } catch (error: any) {
      showToast('Erro ao carregar solicitação', 'error');
      navigate('/erp/produtos/solicitacoes');
    } finally {
      setLoading(false);
    }
  }, [id, showToast, navigate, canEditRequest]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    if (isEdit) {
      loadRequest();
    } else {
      loadNextSku();
    }
  }, [isEdit, loadRequest, loadNextSku]);

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.sku.trim() || !formData.name.trim() || !formData.price) {
      showToast('SKU, nome e preço são obrigatórios', 'error');
      return;
    }

    setSaving(true);
    try {
      const data = {
        sku: formData.sku.trim().toUpperCase(),
        barcode: formData.barcode || null,
        name: formData.name.trim(),
        description: formData.description || null,
        costPrice: formData.costPrice ? parseFloat(formData.costPrice) : null,
        price: parseFloat(formData.price),
        groupId: formData.groupId || null,
        brandId: formData.brandId || null,
        unitId: formData.unitId || null,
        minStock: formData.minStock ? parseInt(formData.minStock, 10) : null,
        maxStock: formData.maxStock ? parseInt(formData.maxStock, 10) : null,
        active: formData.active
      };

      let response;
      if (isEdit && id) {
        response = await productCreationRequestService.update(id, data);
      } else {
        response = await productCreationRequestService.create(data);
      }

      if (response.success) {
        const savedId = isEdit && id ? id : response.data?.id;
        showToast(
          isEdit ? 'Solicitação atualizada com sucesso' : 'Solicitação criada com sucesso',
          'success'
        );
        setTimeout(() => {
          if (savedId) {
            navigate(`/erp/produtos/solicitacoes/${savedId}`);
          } else {
            navigate('/erp/produtos/solicitacoes');
          }
        }, 1000);
      } else {
        showToast(response.message || 'Erro ao salvar solicitação', 'error');
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || 'Erro ao salvar solicitação', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="product-creation-form-loading">
        <Loader2 size={32} className="spinner" />
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="product-creation-form">
      <div className="product-creation-form-header">
        <button
          className="product-creation-form-back"
          onClick={() => navigate(isEdit && id ? `/erp/produtos/solicitacoes/${id}` : '/erp/produtos/solicitacoes')}
        >
          <ArrowLeft size={20} />
        </button>
        <h1>{isEdit ? 'Editar Solicitação de Produto' : 'Nova Solicitação de Produto'}</h1>
      </div>

      <div className="product-creation-form-content">
        <div className="product-creation-form-card">
          <div className="product-creation-form-grid">
            <FormField
              label="SKU"
              name="sku"
              value={formData.sku}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('sku', e.target.value.toUpperCase())
              }
              required
              placeholder={isEdit ? '' : 'Gerado automaticamente'}
              disabled={!isEdit && loadingSku}
              error={null}
              mask={null}
            />

            <FormField
              label="Código de Barras"
              name="barcode"
              value={formData.barcode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('barcode', e.target.value)
              }
              error={null}
              placeholder=""
              mask={null}
            />

            <div className="product-creation-form-full">
              <FormField
                label="Nome"
                name="name"
                value={formData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('name', e.target.value)
                }
                required
                error={null}
                placeholder=""
                mask={null}
              />
            </div>

            <div className="product-creation-form-full">
              <FormField
                label="Descrição"
                name="description"
                type="textarea"
                value={formData.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handleChange('description', e.target.value)
                }
                rows={3}
                error={null}
                placeholder=""
                mask={null}
              />
            </div>

            <FormField
              label="Preço de Custo"
              name="costPrice"
              type="number"
              step="0.01"
              value={formData.costPrice}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('costPrice', e.target.value)
              }
              error={null}
              placeholder=""
              mask={null}
            />

            <FormField
              label="Preço de Venda"
              name="price"
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('price', e.target.value)
              }
              required
              error={null}
              placeholder=""
              mask={null}
            />

            <FormField
              label="Grupo"
              name="groupId"
              type="select"
              value={formData.groupId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                handleChange('groupId', e.target.value)
              }
              options={[
                { value: '', label: 'Selecione...' },
                ...grupos.map((group: any) => ({
                  value: group.id,
                  label: group.name
                }))
              ]}
              error={null}
              placeholder=""
              mask={null}
            />

            <FormField
              label="Marca"
              name="brandId"
              type="select"
              value={formData.brandId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                handleChange('brandId', e.target.value)
              }
              options={[
                { value: '', label: 'Selecione...' },
                ...marcas.map((brand: any) => ({
                  value: brand.id,
                  label: brand.name
                }))
              ]}
              error={null}
              placeholder=""
              mask={null}
            />

            <FormField
              label="Unidade"
              name="unitId"
              type="select"
              value={formData.unitId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                handleChange('unitId', e.target.value)
              }
              options={[
                { value: '', label: 'Selecione...' },
                ...unidades.map((unit: any) => ({
                  value: unit.id,
                  label: `${unit.name}${unit.code ? ` (${unit.code})` : ''}`
                }))
              ]}
              error={null}
              placeholder=""
              mask={null}
            />

            <FormField
              label="Status"
              name="active"
              type="select"
              value={formData.active ? 'true' : 'false'}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                handleChange('active', e.target.value === 'true')
              }
              options={[
                { value: 'true', label: 'Ativo' },
                { value: 'false', label: 'Inativo' }
              ]}
              error={null}
              placeholder=""
              mask={null}
            />

            <FormField
              label="Estoque Mínimo"
              name="minStock"
              type="number"
              value={formData.minStock}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('minStock', e.target.value)
              }
              error={null}
              placeholder=""
              mask={null}
            />

            <FormField
              label="Estoque Máximo"
              name="maxStock"
              type="number"
              value={formData.maxStock}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('maxStock', e.target.value)
              }
              error={null}
              placeholder=""
              mask={null}
            />
          </div>
        </div>

        <div className="product-creation-form-actions">
          <button
            type="button"
            onClick={() => navigate(isEdit && id ? `/erp/produtos/solicitacoes/${id}` : '/erp/produtos/solicitacoes')}
            className="product-creation-form-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="product-creation-form-save"
          >
            {saving ? <Loader2 size={18} className="spinner" /> : <Save size={18} />}
            {saving ? 'Salvando...' : 'Salvar Solicitação'}
          </button>
        </div>
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default ProductCreationRequestFormPage;
